import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Download, Link2 } from 'lucide-react';
import { Combobox } from '../components/Combobox';
import { FilterField } from '../components/FilterField';
import { useDebounce } from '../../../../hooks/useDebounce';
import { useInfiniteScroll } from '../hooks/useInfiniteScroll';
import AssociationsDialog, { AssocItem } from '../components/AssociationsDialog';
import { useAbbinatoreFacets } from '../hooks/useAbbinatoreFacets';
import {
  fetchArticles, fetchArticleMachines,
  fetchMachines, createAssociations, deleteAssociations,
  ArticleRow, ArticleMachine,
} from '../api';

const PAGE_SIZE = 250;
const ROW_HEIGHT = 36;

export default function ArticoliSection() {
  const [searchParams] = useSearchParams();
  const initialSearch = searchParams.get('search') || '';

  // Filters (ordine richiesto: Marca → Categoria → Testo)
  const [brand, setBrand] = useState<string | null>(null);
  const [category, setCategory] = useState<string | null>(null);
  const [search, setSearch] = useState(initialSearch);
  const [unmatched, setUnmatched] = useState(false);
  const debouncedSearch = useDebounce(search, 300);

  // Dominio `articles`: le marche vengono da `products.brand`, non da quelle
  // delle macchine — altrimenti le marche solo-catalogo non comparirebbero.
  const { marche, categorie } = useAbbinatoreFacets('articles', brand, {
    value: category, onChange: setCategory,
  });

  // Data
  const [rows, setRows] = useState<ArticleRow[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);
  const [loading, setLoading] = useState(false);
  const [hasMore, setHasMore] = useState(true);

  // UI state
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [dialogSku, setDialogSku] = useState<string | null>(null);
  const [dialogDesc, setDialogDesc] = useState<string>('');
  const [dialogItems, setDialogItems] = useState<ArticleMachine[] | null>(null);
  const [dialogLoading, setDialogLoading] = useState(false);

  const sentinelRef = useRef<HTMLDivElement>(null);
  const scrollRef = useRef<HTMLDivElement>(null);

  const filterSig = useMemo(
    () => JSON.stringify([debouncedSearch, brand, category, unmatched]),
    [debouncedSearch, brand, category, unmatched],
  );

  useEffect(() => {
    setRows([]); setPage(0); setHasMore(true);
    if (scrollRef.current) scrollRef.current.scrollTop = 0;
  }, [filterSig]);

  useEffect(() => {
    let cancel = false;
    setLoading(true);
    fetchArticles({
      search: debouncedSearch || undefined,
      category: category || undefined,
      brand: brand || undefined,
      unmatched,
      page,
      pageSize: PAGE_SIZE,
    }).then(r => {
      if (cancel) return;
      setRows(prev => page === 0 ? r.items : [...prev, ...r.items]);
      setTotal(r.total);
      setHasMore((page + 1) * PAGE_SIZE < r.total && r.items.length > 0);
    }).finally(() => !cancel && setLoading(false));
    return () => { cancel = true; };
  }, [filterSig, page]);  // eslint-disable-line react-hooks/exhaustive-deps

  useInfiniteScroll(sentinelRef, () => {
    if (!loading && hasMore) setPage(p => p + 1);
  }, hasMore && !loading);

  const brandOpts = useMemo(() => marche.map(m => ({ code: m.code, label: m.label })), [marche]);
  const catOpts = useMemo(() => categorie.map(c => ({ code: c.code, label: c.label })), [categorie]);

  const allSel = rows.length > 0 && rows.every(r => selected.has(r.sku));
  const toggleAll = () => {
    const s = new Set(selected);
    if (allSel) rows.forEach(r => s.delete(r.sku));
    else rows.forEach(r => s.add(r.sku));
    setSelected(s);
  };
  const toggleOne = (sku: string) => {
    const s = new Set(selected);
    if (s.has(sku)) s.delete(sku); else s.add(sku);
    setSelected(s);
  };

  const openDialog = (sku: string, desc: string) => {
    setDialogSku(sku); setDialogDesc(desc);
    setDialogItems(null); setDialogLoading(true);
    fetchArticleMachines(sku)
      .then(items => setDialogItems(items))
      .catch(() => setDialogItems([]))
      .finally(() => setDialogLoading(false));
  };

  const dialogAssocItems: AssocItem[] = useMemo(() => {
    if (!dialogItems) return [];
    return dialogItems.map(m => ({
      id: m.machineId,
      code: m.code,
      brand: m.brand,
      primary: m.model || m.machine,
      secondary: m.machine !== `${m.brand} ${m.model}` ? m.machine : undefined,
    }));
  }, [dialogItems]);

  const refreshDialog = () => {
    if (!dialogSku) return;
    setDialogLoading(true);
    fetchArticleMachines(dialogSku)
      .then(setDialogItems)
      .catch(() => setDialogItems([]))
      .finally(() => setDialogLoading(false));
  };

  const onAddMachineSearch = async (q: string) => {
    const r = await fetchMachines({ search: q, pageSize: 20 });
    return r.items.map(m => ({
      id: m.id,
      primary: m.model,
      secondary: m.brand,
      meta: m.type || undefined,
    }));
  };

  const onAddMachineSelect = async (item: { id: string }) => {
    if (!dialogSku) return;
    await createAssociations({ skus: [dialogSku], machineIds: [item.id] });
  };

  const onRemoveMachines = async (machineIds: string[]) => {
    if (!dialogSku) return;
    await deleteAssociations([dialogSku], machineIds);
  };

  return (
    <div className="flex flex-col h-full" data-testid="articoli-section">
      {/* Filtri orizzontali compatti (label a lato, ordine: Marca → Categoria → Testo) */}
      <div className="flex items-center gap-4 flex-wrap bg-white border border-[#e2e2df] rounded-[10px] px-3.5 py-2.5 mb-3.5 flex-none">
        <FilterField label="Marca" className="w-[300px] flex-none">
          <Combobox
            value={brand}
            onChange={setBrand}
            options={brandOpts}
            placeholder="Tutte le marche"
            testId="filter-article-brand"
          />
        </FilterField>

        <FilterField label="Categoria" className="w-[220px] flex-none">
          <Combobox
            value={category}
            onChange={setCategory}
            options={catOpts}
            placeholder="Tutte le categorie"
            testId="filter-article-category"
          />
        </FilterField>

        <FilterField label="Cerca" className="flex-1 min-w-[220px]">
          <input
            type="text"
            value={search}
            onChange={e => setSearch(e.target.value)}
            placeholder="SKU o testo libero…"
            data-testid="filter-article-search"
            className="w-full px-2.5 py-[6px] border border-[#d8d8d4] rounded-[7px] text-[14px] focus:outline-none focus:border-[#2b3a8f]"
          />
        </FilterField>

        <label className="flex items-center gap-2 text-[14px] text-[#5c5c57] cursor-pointer flex-none">
          <input type="checkbox" checked={unmatched} onChange={e => setUnmatched(e.target.checked)} data-testid="filter-unmatched-article" />
          Solo senza compatibilità
        </label>
      </div>

      {/* Tabella */}
      <div className="flex items-center justify-between gap-3 mb-3.5 flex-none">
        <p className="text-[14px] font-mono text-[#5c5c57]">
          {total.toLocaleString('it')} risultati · {rows.length} caricati
          {selected.size > 0 && (
            <>
              {' · '}
              <span className="text-[#2b3a8f] font-semibold">{selected.size} selezionati</span>
            </>
          )}
        </p>

        {selected.size > 0 ? (
          <div
            className="flex items-center gap-2 px-3 py-1.5 bg-[#eef1fb] border border-[#d4dcf7] rounded-[9px] flex-none"
            data-testid="bulk-bar-articles"
          >
            <button
              onClick={() => setSelected(new Set())}
              className="text-[13px] text-[#5c6bb0] hover:underline px-1"
              data-testid="bulk-deselect-articles"
            >
              deseleziona
            </button>
            <div className="w-px h-4 bg-[#d4dcf7]" />
            <button className="text-[14px] font-semibold text-[#2b3a8f] bg-white border border-[#c3cdf2] px-2.5 py-1.5 rounded-[7px] hover:bg-[#fafbff]">Abbina a macchine →</button>
            <button className="text-[14px] font-semibold text-[#5c5c57] bg-white border border-[#cfcfca] px-2.5 py-1.5 rounded-[7px] hover:bg-[#fafafa]">Copia associazioni</button>
          </div>
        ) : (
          <button className="px-3.5 py-2 border border-[#cfcfca] rounded-lg bg-white text-[14px] font-semibold text-[#3a3a36] hover:bg-[#fafafa] flex items-center gap-1.5 flex-none">
            <Download className="w-3.5 h-3.5" /> Esporta CSV
          </button>
        )}
      </div>

      <div className="bg-white border border-[#e2e2df] rounded-[11px] overflow-hidden flex flex-col flex-1 min-h-0">
          <div className="grid grid-cols-[34px_110px_1.8fr_1.1fr_90px_130px] bg-[#fafafa] border-b border-[#e4e4e1] flex-none">
            <div className="py-2.5 px-3 text-center"><input type="checkbox" checked={allSel} onChange={toggleAll} /></div>
            <div className="py-2.5 px-3 text-[11px] font-bold tracking-[0.08em] text-[#5c5c57] font-mono">CODICE</div>
            <div className="py-2.5 px-3 text-[11px] font-bold tracking-[0.08em] text-[#5c5c57] font-mono">DESCRIZIONE</div>
            <div className="py-2.5 px-3 text-[11px] font-bold tracking-[0.08em] text-[#5c5c57] font-mono">CATEGORIA</div>
            <div className="py-2.5 px-3 text-[11px] font-bold tracking-[0.08em] text-[#5c5c57] font-mono text-right">MACCHINE</div>
            <div className="py-2.5 px-3" />
          </div>

          <div ref={scrollRef} className="flex-1 overflow-y-auto" data-testid="articles-scroll">
            {rows.length === 0 && !loading ? (
              <div className="px-4 py-16 text-center text-[14px] text-[#5c5c57]">Nessun articolo trovato</div>
            ) : (
              <>
                {rows.map(a => {
                  const sel = selected.has(a.sku);
                  return (
                    <div
                      key={a.sku}
                      data-testid={`article-row-${a.sku}`}
                      style={{ height: ROW_HEIGHT }}
                      className={`grid grid-cols-[34px_110px_1.8fr_1.1fr_90px_130px] border-b border-[#f1f1ee] items-center transition-colors ${sel ? 'bg-[#fafbff]' : 'hover:bg-[#fafafa]'}`}
                    >
                      <div className="px-3 text-center">
                        <input type="checkbox" checked={sel} onChange={() => toggleOne(a.sku)} />
                      </div>
                      <div className="px-3 text-[14px] font-semibold font-mono text-[#1f1f1d]">{a.sku}</div>
                      <div className="px-3 text-[15px] text-[#3a3a36] truncate">{a.desc}</div>
                      <div className="px-3 text-[14px] text-[#5c5c57]">{a.cat ?? '—'}</div>
                      <div className="px-3 text-[15px] font-semibold text-[#3a3a36] text-right">{a.compat}</div>
                      <div className="px-3">
                        <button
                          onClick={() => openDialog(a.sku, a.desc)}
                          data-testid={`vedi-article-${a.sku}`}
                          className="text-[14px] font-semibold text-[#2b3a8f] hover:underline flex items-center gap-1"
                          title="Visualizza macchine compatibili"
                        >
                          <Link2 className="w-3 h-3" /> vedi
                        </button>
                      </div>
                    </div>
                  );
                })}
                <div ref={sentinelRef} className="h-8 flex items-center justify-center text-[13px] font-mono text-[#5c5c57]">
                  {loading
                    ? 'Caricamento…'
                    : hasMore
                    ? '↓ scorri per caricare altri'
                    : rows.length > 0 ? `fine elenco · ${rows.length} risultati` : ''}
                </div>
              </>
            )}
          </div>
        </div>

        <AssociationsDialog
        open={dialogSku !== null}
        onClose={() => setDialogSku(null)}
        title={dialogSku ? `${dialogSku} — ${dialogDesc}` : ''}
        subtitle="Macchine compatibili"
        primaryColumnLabel="MODELLO"
        items={dialogAssocItems}
        loading={dialogLoading}
        filterPlaceholder="cerca per marca, codice o modello…"
        testIdPrefix="article-assoc"
        onAddSearch={onAddMachineSearch}
        onAddSelect={onAddMachineSelect}
        addSearchPlaceholder="cerca macchina per modello, marca…"
        onRemove={onRemoveMachines}
        onRefresh={refreshDialog}
      />
    </div>
  );
}
